import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { CocoEvent, useTracking } from '@cotera/client/app/etc';
import { Text, Tooltip, toast } from '@cotera/client/app/components/ui';
import { Icon } from '@cotera/client/app/components/ui/icon/icon';
import { Button } from '@cotera/client/v0/components/ui/button';
import { formatCredits } from '@cotera/client/v0/components/topbar/credit-balance-section';
import { useAppModel } from '../shell/app.model';
import {
  ModelScopeFactory,
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import {
  ArtifactPanelProvider,
  type ArtifactPanelContextValue,
  type ArtifactPanelViewEntry,
} from '@cotera/client/v0/chat/artifact-panel-context';
import {
  popViewEntry,
  pushViewEntry,
} from '@cotera/client/v0/chat/artifact-panel-navigation';
import {
  ResourcePageShell,
  ResourcePageShellBackButton,
  type BreadcrumbItem,
} from '../resources/resource-page-shell';
import {
  runDetailsCrumbLabel,
  workflowRunsBreadcrumbs,
} from './workflow-breadcrumbs';
import {
  WorkflowRunAttemptBadge,
  WorkflowRunStatusBadge,
  WorkflowRunTestBadge,
  LiveDuration,
  LiveRelativeTime,
} from './workflow-run-status';
import { runTimingVisibility } from './workflow-run-timing';
import { WorkflowRunGraph } from './workflow-run-graph';
import { WorkflowRunStepStackContent } from './workflow-run-step-stack-view';
import {
  isWorkflowRunStepViewPayload,
  type WorkflowRunStepViewPayload,
} from './workflow-artifact-views';
import { WorkflowRunDetailModel } from './workflow-run-detail.model';
import { workflowRunShareUrl } from './run-url-sync';
import { WorkflowRunModel, useWorkflowRunModel } from './workflow-run.model';
import {
  WorkflowRunActionTargetsModel,
  canCancelWorkflowRun,
  canRetryWorkflowRun,
} from './workflow-run-action-targets.model';
import { CancelWorkflowRunsAction } from './cancel-workflow-runs.action';
import { RetryWorkflowRunsAction } from './retry-workflow-runs.action';
import type { WorkflowRunDetail } from './workflow-run.types';

const formatTimestamp = (date: Date | null): string =>
  date === null
    ? '—'
    : date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });

const RunMetaItem: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <span>
    <span className="text-muted-foreground/70">{label}:</span> {value}
  </span>
);

/**
 * Credits spent by the run, with an LLM/Tools breakdown on hover. Mirrors the
 * chat cost badge: gated on the `showCredits` preference and hidden when the
 * run has no billable usage (e.g. runs predating cost tracking, or with no
 * agent/tool nodes).
 */
const RunCostMetaItem: React.FC<{ cost: WorkflowRunDetail['cost'] }> = ({
  cost,
}) => {
  const appModel = useAppModel();
  const { showCredits } = useWatchableValue(appModel.uiPreferences);

  if (!showCredits) {
    return null;
  }
  if (cost.credits === 0) {
    return null;
  }

  return (
    <Tooltip
      side="bottom"
      tooltipContent={
        <div className="flex flex-col gap-1 text-xs min-w-[140px]">
          <div className="flex justify-between gap-4">
            <span className="text-foreground/70">This run</span>
            <span className="font-medium">
              {formatCredits(cost.credits)} cr
            </span>
          </div>
          {cost.breakdown.llm > 0 && (
            <div className="flex justify-between gap-4">
              <span className="text-foreground/70">LLM</span>
              <span>{formatCredits(cost.breakdown.llm)} cr</span>
            </div>
          )}
          {cost.breakdown.toolcall > 0 && (
            <div className="flex justify-between gap-4">
              <span className="text-foreground/70">Tools</span>
              <span>{formatCredits(cost.breakdown.toolcall)} cr</span>
            </div>
          )}
        </div>
      }
    >
      <span className="flex items-center gap-1 cursor-default select-none">
        <span className="text-muted-foreground/70">Cost:</span>
        <Icon icon="credits" />
        <span aria-label={`Run cost: ${formatCredits(cost.credits)} credits`}>
          {formatCredits(cost.credits)} cr
        </span>
      </span>
    </Tooltip>
  );
};

/**
 * Copy `text`, reporting whether it landed. `navigator.clipboard` is undefined
 * outside a secure context and the write itself rejects when the document isn't
 * focused, so callers surface the value instead of failing silently.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard === undefined) {
    return false;
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** A `copied` flag that clears itself, cleaned up if the run closes first. */
function useTransientCopied(): [boolean, () => void] {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return [copied, useCallback(() => setCopied(true), [])];
}

/**
 * Copies a link to this run to the clipboard — this run, not the workflow: an
 * editor panel shows one of many runs, and the link has to name the one on
 * screen. See {@link workflowRunShareUrl} for why that is the standalone run
 * page rather than the editor URL the sharer is on.
 */
const ShareRunButton: React.FC<{ shareUrl: string }> = ({ shareUrl }) => {
  const [copied, markCopied] = useTransientCopied();

  const handleShare = useCallback(() => {
    void (async () => {
      if (!(await copyToClipboard(shareUrl))) {
        toast.error('Could not copy the link', { description: shareUrl });
        return;
      }
      markCopied();
      toast.success('Run link copied');
    })();
  }, [shareUrl, markCopied]);

  return (
    // Outline, not primary: in the chat panel this sits a few pixels below the
    // page's own primary Share, and two navy buttons with the same icon and word
    // that share different things read as one repeated button. The page keeps
    // the only primary; the run's actions read as a group under it. "Share run"
    // for the same reason — the two labels are visible together.
    <Button
      variant="outline"
      size="sm"
      icon={copied ? 'check' : 'share'}
      onClick={handleShare}
      title="Copy a link to this run"
      aria-label="Copy a link to this run"
    >
      <span className="hidden @md/run-header:inline">
        {copied ? 'Copied' : 'Share run'}
      </span>
    </Button>
  );
};

/** Slim run header: workflow name, status, share/retry/cancel, and timings. */
const WorkflowRunDetailHeader: React.FC<{
  run: WorkflowRunDetail;
  /**
   * Titles the header. Null while it loads, and on that first paint the header
   * falls back to naming the page rather than showing an empty title.
   */
  workflowName: string | null;
  onOpenRunId?: (runId: string) => void;
  /** Shareable link to this run, for the Share button. */
  shareUrl: string;
}> = ({ run, workflowName, onOpenRunId, shareUrl }) => {
  const cancelRunsAction = useAction(CancelWorkflowRunsAction);
  const retryRunsAction = useAction(RetryWorkflowRunsAction);

  const handleCancel = useCallback(() => {
    void cancelRunsAction.execute({});
  }, [cancelRunsAction]);

  // Retry produces a NEW run, so this view would otherwise keep showing the old
  // one — follow the retry to its result.
  const handleRetry = useCallback(() => {
    void retryRunsAction.execute({}).then((result) => {
      const createdRunId = result.isOk() ? result.value.resourceId : undefined;
      if (createdRunId !== undefined) {
        onOpenRunId?.(createdRunId);
      }
    });
  }, [retryRunsAction, onOpenRunId]);

  const timing = runTimingVisibility(run);

  return (
    // Sized against the header's own width, not the viewport's: this renders in
    // an artifact panel that is narrow on a wide screen, so viewport
    // breakpoints would report "desktop" for a 380px column.
    <div className="@container/run-header shrink-0 border-b border-border/40">
      <div className="px-4 pt-4 pb-3 @md/run-header:px-6 @md/run-header:pt-6 @md/run-header:pb-4">
        <div className="flex items-start justify-between gap-3">
          {/* Identity and actions are separate columns rather than one wrapping
              row: wrapping put the buttons on a line of their own, pushed right
              under an empty gap. Now the actions keep their corner and only the
              identity block wraps. */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
            {/* The workflow, not "Run details": in the editor panel this
                header is the only thing naming what you are looking at, and a
                shared link can land someone here with no other context.
                `text-foreground` rather than a hardcoded slate, so the title
                inverts with the theme; truncated so a long name ellipsizes
                instead of squeezing the badges off the row. */}
            <Text.Title
              type="title"
              className="min-w-0 truncate text-foreground !text-xl !font-bold !font-serif @md/run-header:!text-2xl"
            >
              {workflowName ?? 'Run details'}
            </Text.Title>
            <WorkflowRunStatusBadge status={run.status} size="default" />
            {run.isTest ? <WorkflowRunTestBadge size="default" /> : null}
            <WorkflowRunAttemptBadge retryNum={run.retryNum} size="default" />
            {/* Dot and time wrap as one unit — a separator orphaned at the
                start of a wrapped line reads as a bullet. Anchored to the
                start, falling back to creation for a run still queued, so
                "4d ago" reads as when the work happened. Hidden while the run
                is going, where it would restate the elapsed counter below. */}
            {timing.showRelativeAge ? (
              <span className="flex items-center gap-3">
                <span className="text-muted-foreground/50">·</span>
                <LiveRelativeTime
                  date={run.startedAt ?? run.createdAt}
                  className="text-sm text-muted-foreground"
                  title={formatTimestamp(run.startedAt ?? run.createdAt)}
                />
              </span>
            ) : null}
          </div>
          {/* Labels name their object — "Cancel" alone is the word for dismissing
              a view, and this one kills a running job beside a panel that has
              its own close button. Labels drop below 28rem, leaving icon
              buttons; every one carries an aria-label and title, so the meaning
              survives the label. */}
          <div className="flex shrink-0 items-center gap-2">
            <ShareRunButton shareUrl={shareUrl} />
            {/* Retrying an already-superseded attempt would fork the chain into
                two leaves and put two rows back in the runs list, so the newer
                attempt is the only place Retry is offered. */}
            {run.retriedByRunId === null && canRetryWorkflowRun(run) ? (
              <Button
                variant="outline"
                size="sm"
                icon="refresh-cw"
                onClick={handleRetry}
                title="Retry run"
                aria-label="Retry run"
              >
                <span className="hidden @md/run-header:inline">Retry run</span>
              </Button>
            ) : null}
            {canCancelWorkflowRun(run) ? (
              <Button
                variant="outline"
                size="sm"
                icon="x-circle"
                onClick={handleCancel}
                title="Cancel run"
                aria-label="Cancel run"
              >
                <span className="hidden @md/run-header:inline">Cancel run</span>
              </Button>
            ) : null}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground @md/run-header:gap-x-6">
          {timing.showCreated ? (
            <RunMetaItem
              label="Created"
              value={formatTimestamp(run.createdAt)}
            />
          ) : null}
          {timing.showStarted ? (
            <RunMetaItem
              label="Started"
              value={formatTimestamp(run.startedAt)}
            />
          ) : null}
          {timing.showFinished ? (
            <RunMetaItem
              label="Finished"
              value={formatTimestamp(run.finishedAt)}
            />
          ) : null}
          {timing.showDuration ? (
            <RunMetaItem
              label={timing.durationLabel}
              value={
                <LiveDuration
                  startedAt={run.startedAt}
                  finishedAt={run.finishedAt}
                />
              }
            />
          ) : null}
          <RunCostMetaItem cost={run.cost} />
          {run.idempotencyKey !== null && run.idempotencyKey !== '' ? (
            <RunMetaItem
              label="Idempotency key"
              value={
                <span className="font-mono break-all">
                  {run.idempotencyKey}
                </span>
              }
            />
          ) : null}
          {run.retryOf !== null && onOpenRunId !== undefined ? (
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => onOpenRunId(run.retryOf!)}
            >
              View previous attempt
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/**
 * Shown on an attempt that a newer one has replaced. Superseded attempts are
 * hidden from the runs list — a retried run reads as that one run being in
 * progress again — so this is the only signal that this is not the live attempt.
 */
const WorkflowRunSupersededBanner: React.FC<{
  retriedByRunId: string;
  onOpenRunId?: (runId: string) => void;
}> = ({ retriedByRunId, onOpenRunId }) => (
  <div className="shrink-0 flex flex-wrap items-center gap-3 border-b border-border/40 bg-muted/40 px-6 py-2">
    <Text.P className="text-sm text-muted-foreground">
      This attempt was retried. It no longer appears in the runs list.
    </Text.P>
    {onOpenRunId !== undefined ? (
      <Button
        variant="outline"
        size="sm"
        onClick={() => onOpenRunId(retriedByRunId)}
      >
        Open latest attempt
      </Button>
    ) : null}
  </div>
);

/**
 * Run detail body (no page shell): a slim run header above the read-only run
 * graph, driven by the in-scope {@link WorkflowRunModel}. Clicking a node opens
 * its step view on the ambient artifact-panel stack. Places a
 * {@link WorkflowRunDetailModel} in scope so the command palette can surface
 * developer details for the run via {@link ShowDeveloperDetailsAction}.
 */
export const WorkflowRunDetailEmbedded: React.FC<{
  /**
   * Opens another run by id. Supplied by the host because the standalone route
   * navigates while the workflow editor pushes onto its artifact-panel stack.
   */
  onOpenRunId?: (runId: string) => void;
}> = ({ onOpenRunId }) => {
  const org = useWhoami((s) => s.org);
  const model = useWorkflowRunModel();
  const { workflowId, runId } = model;
  const run = useWatchableValue(model.runWatchable);
  const workflowName = useWatchableValue(model.workflowNameWatchable);

  // Offered everywhere the run detail renders, the standalone page included:
  // that page *is* the shared link, and whoever opens it is the next person
  // likely to pass it on — re-copying it out of the address bar means picking
  // the run id out of a URL by hand.
  const shareUrl = workflowRunShareUrl({
    origin: window.location.origin,
    orgSlug: org.slug,
    workflowId,
    runId,
  });

  useTracking(CocoEvent.WorkflowRunViewed, { workflowId, runId });

  // Build the dev-details model inside the scope factory (registered in a
  // deps-compared effect) so it registers once per run instead of every render.
  // The old `<ModelScope models={[...]}>` re-ran add/removeModel each render, and
  // each call fires the actions context `notify()`, waking in-scope-model
  // subscribers and looping forever once a run is loaded.
  const createRunDetailModels = useCallback(
    () =>
      run === null
        ? []
        : [
            new WorkflowRunDetailModel({
              orgId: org.id,
              runId,
              workflowId,
              triggerId: run.triggerId,
              retryOf: run.retryOf,
              retryNum: run.retryNum,
            }),
            // The detail view always targets exactly the run it is showing, so
            // the shared cancel/retry actions work here with no extra plumbing.
            new WorkflowRunActionTargetsModel({
              workflowId,
              scopeKey: `run:${runId}`,
              runs: [{ id: run.id, status: run.status }],
            }),
          ],
    [org.id, workflowId, runId, run]
  );

  if (run === null) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Text.P className="text-muted-foreground">Loading...</Text.P>
      </div>
    );
  }

  return (
    <ModelScopeFactory
      createModels={createRunDetailModels}
      deps={[
        org.id,
        workflowId,
        runId,
        run.status,
        run.triggerId,
        run.retryOf,
        run.retryNum,
        run.retriedByRunId,
      ]}
    >
      <div className="h-full flex flex-col min-h-0 bg-background">
        <WorkflowRunDetailHeader
          run={run}
          workflowName={workflowName}
          onOpenRunId={onOpenRunId}
          shareUrl={shareUrl}
        />
        {run.retriedByRunId !== null ? (
          <WorkflowRunSupersededBanner
            retriedByRunId={run.retriedByRunId}
            onOpenRunId={onOpenRunId}
          />
        ) : null}
        <div className="min-h-0 flex-1 overflow-hidden">
          <WorkflowRunGraph />
        </div>
      </div>
    </ModelScopeFactory>
  );
};

/**
 * {@link WorkflowRunDetailEmbedded} plus the {@link WorkflowRunModel} it needs,
 * for hosts that show a run inside someone else's page — the workflow editor's
 * artifact stack, or an artifact link pushed from another artifact's stack.
 * The standalone route builds its own scope higher up so the breadcrumbs can read
 * the workflow name.
 *
 * `stepView` swaps the run graph for one node's step detail, keeping the run
 * model mounted underneath. Hosts that own a view stack pass the step their
 * stack is showing; the run itself never picks it, since the click that pushed
 * it landed on the host's stack.
 */
export const WorkflowRunStackContent: React.FC<{
  workflowId: string;
  runId: string;
  onOpenRunId?: (runId: string) => void;
  stepView?: WorkflowRunStepViewPayload | null;
}> = ({ workflowId, runId, onOpenRunId, stepView = null }) => {
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org?.id) ?? '';
  const createModels = useCallback(
    () => [
      new WorkflowRunModel({ workflowId, runId, orgId, client, queryClient }),
    ],
    [workflowId, runId, orgId, client, queryClient]
  );
  return (
    <ModelScopeFactory
      createModels={createModels}
      deps={[workflowId, runId, orgId]}
      key={`run:${runId}`}
    >
      {stepView !== null ? (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <WorkflowRunStepStackContent view={stepView} />
        </div>
      ) : (
        <WorkflowRunDetailEmbedded onOpenRunId={onOpenRunId} />
      )}
    </ModelScopeFactory>
  );
};

const WorkflowRunDetailInner: React.FC<{
  workflowId: string;
  runId: string;
}> = ({ workflowId, runId }) => {
  const org = useWhoami((s) => s.org);
  const navigate = useNavigate();
  const model = useWorkflowRunModel();
  const workflowName = useWatchableValue(model.workflowNameWatchable);
  const [viewStack, setViewStack] = useState<ArtifactPanelViewEntry[]>([]);

  const handleOpenRunId = useCallback(
    (nextRunId: string) => {
      navigate(`/${org.slug}/workflows/${workflowId}/runs/${nextRunId}`);
    },
    [navigate, org.slug, workflowId]
  );

  // Standalone runs have no chat artifact panel, so we host a local stack with
  // the same API — node clicks push step "pages" exactly like the chat panel.
  const artifactPanel = useMemo<ArtifactPanelContextValue>(
    () => ({
      isExpanded: false,
      toggleExpanded: () => undefined,
      // Standalone runs have no panel to expand.
      expand: () => undefined,
      canExpand: false,
      isHidden: false,
      revealArtifacts: () => undefined,
      focusArtifact: () => undefined,
      openArtifact: () => undefined,
      canOpenArtifacts: false,
      closeArtifact: () => undefined,
      closeBackgroundArtifact: () => undefined,
      pushView: (entry, options) =>
        setViewStack((stack) => pushViewEntry(stack, entry, options)),
      popView: () => setViewStack((stack) => popViewEntry(stack)),
      resetViewStack: () => setViewStack([]),
      canPopView: viewStack.length > 0,
      viewStack,
      activeArtifactId: null,
    }),
    [viewStack]
  );

  const top = viewStack[viewStack.length - 1];
  const activeStepView =
    top !== undefined && isWorkflowRunStepViewPayload(top.payload)
      ? top.payload
      : null;

  const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
    const base = workflowRunsBreadcrumbs({
      orgSlug: org.slug,
      workflowId,
      workflowName,
    });
    if (activeStepView !== null) {
      return [
        ...base,
        {
          label: runDetailsCrumbLabel(runId),
          onClick: () => setViewStack([]),
        },
        { label: 'Step details' },
      ];
    }
    return [...base, { label: runDetailsCrumbLabel(runId) }];
  }, [org.slug, workflowId, workflowName, runId, activeStepView]);

  return (
    <ArtifactPanelProvider value={artifactPanel}>
      <ResourcePageShell
        breadcrumbs={breadcrumbs}
        // White, though the run is read-only and `reader` is the default for
        // that. The same run detail is white in the chat's artifact panel,
        // which paints its own surface regardless, and the workflow it belongs
        // to is an `editor` page — so the canvas was the odd one out, and a
        // shared link landed on a different ground than the view it was copied
        // from.
        surface="editor"
        leading={
          activeStepView !== null ? (
            <ResourcePageShellBackButton
              onClick={() => setViewStack((stack) => popViewEntry(stack))}
            />
          ) : undefined
        }
      >
        {activeStepView !== null ? (
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <WorkflowRunStepStackContent view={activeStepView} />
          </div>
        ) : (
          <WorkflowRunDetailEmbedded onOpenRunId={handleOpenRunId} />
        )}
      </ResourcePageShell>
    </ArtifactPanelProvider>
  );
};

const WorkflowRunDetailContent: React.FC<{
  workflowId: string;
  runId: string;
}> = ({ workflowId, runId }) => {
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const createModels = useCallback(
    () => [
      new WorkflowRunModel({ workflowId, runId, orgId, client, queryClient }),
    ],
    [workflowId, runId, orgId, client, queryClient]
  );
  return (
    <ModelScopeFactory
      createModels={createModels}
      deps={[workflowId, runId, orgId]}
      key={`${workflowId}:${runId}`}
    >
      <WorkflowRunDetailInner workflowId={workflowId} runId={runId} />
    </ModelScopeFactory>
  );
};

export const WorkflowRunDetailPage: React.FC = () => {
  const { workflowId, runId } = useParams<{
    workflowId: string;
    runId: string;
  }>();
  if (
    workflowId === undefined ||
    workflowId === '' ||
    runId === undefined ||
    runId === ''
  ) {
    return null;
  }
  return <WorkflowRunDetailContent workflowId={workflowId} runId={runId} />;
};
